/**
 * Shared per-row task actions — mark done / cancel / reopen / edit / delete
 * (maker-checker) / restore — used by BOTH AgreementTasksTab and MyTasksPage
 * so the task lifecycle affordances and permission gating stay identical
 * across the two surfaces (§3h). Extracted 2026-06-29 for #63 (unify Tasks
 * into the central To-Do hub); the logic is lifted verbatim from
 * AgreementTasksTab so behaviour is preserved.
 *
 * The parent page owns the edit modal; this component calls onEdit(task)
 * when the (creator- or Admin-gated) Edit icon is clicked.
 *
 * Admins hold every privilege over every task, whoever created it (Pedro
 * 2026-10-10) — mirrors `_is_admin` in backend routers/agreement_tasks.py.
 */
import { ActionIcon, Group, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowBackUp, IconCheck, IconEdit, IconRefresh, IconTrash, IconX,
} from '@tabler/icons-react';

import { useCurrentUser } from '../hooks/useAuth';
import {
  useDeleteAgreementTask, useRestoreAgreementTask,
  useUpdateAgreementTaskStatus,
} from '../hooks/useAgreementTasks';
import type { AgreementTask, AgreementTaskStatus } from '../api/types';
import { useAlertModal } from './AlertModalProvider';
import { useConfirmDelete } from '../hooks/useConfirmDelete';

interface Props {
  task: AgreementTask;
  /** Open the edit modal for this task (owned by the parent page). */
  onEdit: (task: AgreementTask) => void;
}

export function TaskRowActions({ task, onEdit }: Props) {
  const { user } = useCurrentUser();
  const isChecker = user?.role === 'admin' || user?.role === 'compliance';
  const updateStatus = useUpdateAgreementTaskStatus();
  const del = useDeleteAgreementTask();
  const restore = useRestoreAgreementTask();
  const confirmDelete = useConfirmDelete();
  const { showAlert } = useAlertModal();

  const isAuthor = task.created_by === user?.id;
  const isAssignee = task.assignee_user_id === user?.id;
  const isAdmin = user?.role === 'admin';
  const canManage = isAuthor || isAdmin;              // edit / cancel / reopen / delete
  const canComplete = isAuthor || isAssignee || isAdmin;
  const isDeleted = !!task.deleted_at;

  function handleStatus(next: AgreementTaskStatus) {
    updateStatus.mutate(
      { id: task.id, status: next },
      {
        onSuccess: () => {
          const msg = next === 'DONE' ? 'Task marked done'
                    : next === 'CANCELLED' ? 'Task cancelled'
                    : 'Task reopened';
          notifications.show({ message: msg, color: next === 'OPEN' ? 'blue' : 'green' });
        },
        onError: (err) => {
          showAlert({
            color: 'red', title: 'Could not change status',
            message: err.detail ?? String(err),
          });
        },
      },
    );
  }

  function handleDelete() {
    confirmDelete('task', () => del.mutate(task.id, {
      onSuccess: () => {
        if (!isChecker) {
          showAlert({
            color: 'orange',
            title: 'Filed for compliance approval',
            message: 'Your delete request was filed. The task remains visible until Compliance approves.',
          });
        }
      },
    }));
  }

  function handleRestore() {
    restore.mutate(task.id, {
      onSuccess: () => {
        notifications.show({ message: 'Task restored', color: 'green' });
      },
      onError: (err) => {
        showAlert({
          color: 'red', title: 'Could not restore',
          message: err.detail ?? String(err),
        });
      },
    });
  }

  // Keep the icons on a single row (the narrow Actions column — #35 polish).
  return (
    <Group gap="xs" wrap="nowrap">
      {isDeleted ? (
        isChecker && (
          <Tooltip label="Restore this task" withArrow>
            <ActionIcon variant="subtle" color="green"
              onClick={handleRestore}
              loading={restore.isPending}>
              <IconArrowBackUp size={14} />
            </ActionIcon>
          </Tooltip>
        )
      ) : (
        <>
          {task.status === 'OPEN' && (
            <Tooltip label={
              canComplete
                ? 'Mark done'
                : 'Only the creator, the assignee or an Admin can mark this done'
            }>
              <ActionIcon variant="subtle" color="green"
                disabled={!canComplete}
                onClick={() => handleStatus('DONE')}
                loading={updateStatus.isPending}>
                <IconCheck size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          {task.status === 'OPEN' && (
            <Tooltip label={
              canManage ? 'Mark cancelled' : 'Only the creator or an Admin can cancel a task'
            }>
              <ActionIcon variant="subtle" color="gray"
                disabled={!canManage}
                onClick={() => handleStatus('CANCELLED')}
                loading={updateStatus.isPending}>
                <IconX size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          {(task.status === 'DONE' || task.status === 'CANCELLED') && (
            <Tooltip label={
              canManage ? 'Reopen' : 'Only the creator or an Admin can reopen a task'
            }>
              <ActionIcon variant="subtle" color="blue"
                disabled={!canManage}
                onClick={() => handleStatus('OPEN')}
                loading={updateStatus.isPending}>
                <IconRefresh size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip label={canManage ? 'Edit' : 'Only the creator or an Admin can edit'}>
            <ActionIcon variant="subtle" color="blue"
              disabled={!canManage}
              onClick={() => onEdit(task)}>
              <IconEdit size={14} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={
            canManage
              ? (isChecker
                  ? 'Delete (files change request, auto-approved)'
                  : 'Delete (files change request)')
              : "Only the creator or an Admin can delete a task. A creator's request is approved by Compliance / Admin."
          }>
            <ActionIcon variant="subtle" color="red"
              disabled={!canManage}
              onClick={handleDelete}
              loading={del.isPending}>
              <IconTrash size={14} />
            </ActionIcon>
          </Tooltip>
        </>
      )}
    </Group>
  );
}
